import { loadStats, saveStats, type Stats } from "../storage/local.ts";
import { ensureAuthenticated, getSupabase } from "./supabase.ts";

type JokerLedgerReason =
  | "level_up"
  | "game_spend"
  | "undo_refund"
  | "migration_adjust";

interface JokerWalletRow {
  balance: number;
  lifetime_earned: number;
  lifetime_spent: number;
}

function rowToStats(stats: Stats, row: JokerWalletRow): Stats {
  return {
    ...stats,
    jokerCards: Number(row.balance ?? 0),
    jokerCardsEarned: Number(row.lifetime_earned ?? 0),
    jokerCardsSpent: Number(row.lifetime_spent ?? 0),
  };
}

export async function initJokerSync(): Promise<Stats | null> {
  const sb = getSupabase();
  if (!sb) return null;
  const session = await ensureAuthenticated();
  const userId = session?.user.id;
  if (!userId) return null;

  const local = loadStats();
  const { data, error } = await sb
    .from("joker_wallets")
    .select("balance,lifetime_earned,lifetime_spent")
    .eq("user_id", userId)
    .maybeSingle();

  if (error) {
    console.warn("[joker-sync] wallet pull failed:", error.message);
    return local;
  }

  if (data) {
    const merged = rowToStats(local, data as JokerWalletRow);
    saveStats(merged);
    return merged;
  }

  // Seed the server wallet from existing local-only inventory after the DB
  // migration. This is one-way and idempotent for first server creation.
  if (local.jokerCards > 0) {
    await recordJokerLedger({
      delta: local.jokerCards,
      reason: "migration_adjust",
      idempotencyKey: `wallet-seed:${userId}`,
      note: "Seeded from local inventory",
    });
  }
  return local;
}

export async function recordJokerLedger(opts: {
  delta: number;
  reason: JokerLedgerReason;
  gameSeed?: string;
  idempotencyKey?: string;
  note?: string;
}): Promise<void> {
  if (opts.delta === 0) return;
  const sb = getSupabase();
  if (!sb) return;
  const session = await ensureAuthenticated();
  const userId = session?.user.id;
  if (!userId) return;

  const { error } = await sb.from("joker_ledger").insert({
    user_id: userId,
    delta: opts.delta,
    reason: opts.reason,
    game_seed: opts.gameSeed ?? null,
    idempotency_key: opts.idempotencyKey ?? null,
    note: opts.note ?? null,
  });

  if (error && error.code !== "23505") {
    console.warn("[joker-sync] ledger insert failed:", error.message);
  }
}
